<template>
  <li
    v-tooltip="{
      content: getTooltipContent(webhook.name),
      placement: 'bottom',
      followCursor: 'vertical',
    }"
    data-cy="webhook-item"
    class="cursor-pointer truncate p-2 whitespace-nowrap text-gray-700 hover:bg-gray-100"
    v-on-click-outside="() => (showParameters = false)"
    @mouseenter="checkContentWidth"
  >
    <div class="flex justify-between space-x-2">
      <p class="truncate" data-cy="do-templates-dropdown-option-text">
        {{ webhook.name }}
      </p>
      <div class="flex items-center space-x-2">
        <div class="flex h-4 w-4 items-center justify-center">
          <button
            v-if="parameters.length"
            v-tooltip="parametersTooltipText"
            @click="showParameters = !showParameters"
          >
            <icon-base
              :icon="IconCog"
              class="mt-2 hover:text-primary"
              :class="showParameters ? 'text-active-text' : 'text-label'"
            />
          </button>
        </div>

        <button
          class="rounded bg-gradient-to-bl from-primary-button-1 to-primary-button-2 px-2 py-1 text-xs font-medium text-white shadow-primary-button hover:to-primary-button-hover-2 focus:to-primary-button-active active:from-primary-button-active active:to-primary-button-active"
          data-cy="webhook-run"
          @click="runWebhook"
        >
          {{ $t("COMMON.RUN") }}
        </button>
      </div>
    </div>
    <custom-webhook-parameter-list
      v-if="showParameters"
      class="mt-2"
      :parameters="parameters"
      @parameter:change="updateParameters"
    />
  </li>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import CustomWebhookParameterList from "@/components/communication/CustomWebhookParameterList.vue";
import IconCog from "@/components/icons/IconCog.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type {
  CustomWebhook,
  CustomWebhookTriggerPayload,
} from "@/models/customWebhook";
import type { OptionalWorkflowWebhookParameter } from "@/models/workflows";

const MAX_NAME_LENGTH = 30;

const { t } = useI18n();

const showParameters = ref(false);
const isTextTruncated = ref(false);

const { webhook, showTooltip } = defineProps<{
  webhook: Partial<CustomWebhook>;
  showTooltip: boolean;
}>();

const emit = defineEmits<{
  "webhook:trigger": [CustomWebhookTriggerPayload];
}>();

const parameters = ref(
  webhook.parameters?.map((param) => ({ ...param, enabled: true })) ?? []
);

const parametersTooltipText = computed(() => {
  if (showParameters.value) {
    return `${t("COMMON.HIDE")} ${t("COMMON.PARAMETER", 2)}`;
  }
  return `${t("COMMON.SHOW")} ${t("COMMON.PARAMETER", 2)}`;
});

const getTooltipContent = (name?: string) =>
  name && name.length > MAX_NAME_LENGTH && showTooltip ? name : "";

const checkContentWidth = (event: MouseEvent) => {
  const { offsetWidth, scrollWidth } = event.target as HTMLElement;
  isTextTruncated.value = offsetWidth < scrollWidth;
};

const runWebhook = () => {
  if (!webhook.id) {
    return;
  }

  emit("webhook:trigger", {
    id: webhook.id,
    parameters: parameters.value
      .filter(({ enabled }) => enabled)
      .map(({ name, value }) => ({ name, value })),
  });

  showParameters.value = false;
};

const updateParameters = (
  updatedParameter: OptionalWorkflowWebhookParameter
) => {
  parameters.value = parameters.value.map((parameter) => {
    const { name: uName, type: uType } = updatedParameter;
    const { name, type } = parameter;
    if (name === uName && type === uType) {
      return updatedParameter;
    }
    return parameter;
  });
};
</script>
